import * as amplitude from "@amplitude/analytics-browser";
import { Link } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/table-core";
import { useMemo } from "react";
import { RiHistoryLine } from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import { StatusTag } from "src/components/DataDisplay/StatusTag";
import { NoDataTable } from "src/components/Feedback/NoDataTable";
import { PaginatedTable } from "src/components/Table/PaginatedTable";
import { SortType } from "src/components/Table/SortButton";
import { BulkAction } from "src/generated/hooks/form-service/requests/types.gen";
import { useOrganization } from "src/hooks/useOrganization";
import { formatIsoDateToOrg } from "src/services/format";
import * as Url from "src/services/url";
import {
  ACTION_TYPE_LABELS,
  getActivityStatusMetadata,
  getAppliedToLabel,
} from "./labels";

interface ActivityListProps {
  activities: BulkAction[];
  formTemplateId: string;
  limit: number;
  offset: number;
  count: number;
  showCreatedBy: boolean;
  hasActiveFilters: boolean;
  onFetchMore: (limit: number, offset: number) => void;
  onChangeSort: (columnName: string, sortType: SortType) => void;
  onClearFilters: () => void;
  isLoading?: boolean;
}

const SORTABLE_COLUMN_IDS = [
  "action_type",
  "applied_to",
  "queued",
  "started",
  "done",
  "status",
  "created_by",
];

export const ActivityList: React.FC<ActivityListProps> = ({
  activities,
  formTemplateId,
  limit,
  offset,
  count,
  showCreatedBy,
  hasActiveFilters,
  onFetchMore,
  onChangeSort,
  onClearFilters,
  isLoading = false,
}) => {
  const organization = useOrganization();

  const columns: ColumnDef<BulkAction>[] = useMemo(() => {
    const formatTimestamp = (isoDate: string | null) =>
      isoDate
        ? formatIsoDateToOrg(isoDate, organization, "MM/dd/yyyy h:mm a")
        : "";

    return [
      {
        id: "action_type",
        header: "ACTION TYPE",
        cell: (props) => {
          const activity = props.row.original;
          return (
            <Link
              as={RouterLink}
              color="primary.500"
              textDecoration="underline"
              to={organization
                .map((org) =>
                  Url.OrgAdmin.Forms.activityDetail({
                    organization: org,
                    formTemplateId,
                    bulkActionId: activity.id,
                  })
                )
                .withDefault("#")}
              onClick={() => {
                amplitude.track("Clicked bulk activity detail", {
                  action_type: activity.actionType,
                  bulk_action_id: activity.id,
                  source: "list",
                  status: activity.status,
                });
              }}
            >
              {ACTION_TYPE_LABELS[activity.actionType]}
            </Link>
          );
        },
      },
      {
        id: "applied_to",
        header: "APPLIED TO",
        cell: (props) => getAppliedToLabel(props.row.original),
      },
      ...(showCreatedBy
        ? [
            {
              id: "created_by",
              header: "TAKEN BY",
              cell: (props) => {
                const { createdBy } = props.row.original;
                const name = createdBy.name ?? "";

                return createdBy.personId ? (
                  <Link
                    as={RouterLink}
                    color="primary.500"
                    textDecoration="underline"
                    to={organization
                      .map((org) =>
                        Url.OrgAdmin.Team.editMember(org, createdBy.personId!)
                      )
                      .withDefault("#")}
                  >
                    {name}
                  </Link>
                ) : (
                  name
                );
              },
            } satisfies ColumnDef<BulkAction>,
          ]
        : []),
      {
        id: "queued",
        header: "QUEUED",
        cell: (props) => formatTimestamp(props.row.original.createdAt),
      },
      {
        id: "started",
        header: "STARTED",
        cell: (props) => formatTimestamp(props.row.original.startedAt),
      },
      {
        id: "done",
        header: "FINISHED",
        cell: (props) => formatTimestamp(props.row.original.completedAt),
      },
      {
        id: "status",
        header: "STATUS",
        cell: (props) => (
          <StatusTag
            disableGlossary
            status={getActivityStatusMetadata(props.row.original)}
          />
        ),
      },
    ];
  }, [formTemplateId, organization, showCreatedBy]);

  if (isLoading) {
    return (
      <PaginatedTable<BulkAction>
        data={[]}
        columns={columns}
        limit={limit}
        offset={offset}
        count={0}
        onFetchMore={onFetchMore}
        sortableColumnIds={SORTABLE_COLUMN_IDS}
        onChangeSort={onChangeSort}
        isLoading={true}
      />
    );
  }

  if (!activities.length) {
    return (
      <NoDataTable
        icon={RiHistoryLine}
        text="No activity found"
        buttonText={hasActiveFilters ? "Clear" : undefined}
        onButtonAction={onClearFilters}
      />
    );
  }

  return (
    <PaginatedTable<BulkAction>
      data={activities}
      columns={columns}
      limit={limit}
      offset={offset}
      count={count}
      onFetchMore={onFetchMore}
      sortableColumnIds={SORTABLE_COLUMN_IDS}
      onChangeSort={onChangeSort}
    />
  );
};
